import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FaArrowRight, FaCheckCircle } from "react-icons/fa";

function SubscriptionConfirmation() {
  const [update, setUpdate] = useState(false);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const updateValue = queryParams.get("update");
    setUpdate(Boolean(updateValue && updateValue !== "false" && updateValue !== "0"));
  }, []);

  const content = useMemo(
    () =>
      update
        ? {
            eyebrow: "Subscription updated",
            title: "Your subscription has been updated.",
            description:
              "A confirmation email has been sent with your updated receipt and subscription details.",
            primaryCta: "Open the app",
          }
        : {
            eyebrow: "Subscription confirmed",
            title: "Thank you for subscribing.",
            description:
              "A confirmation email has been sent with your receipt and subscription details.",
            primaryCta: "Start using the app",
          },
    [update]
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/40 to-teal-50/30 font-ui">
      <div className="mx-auto flex min-h-screen max-w-7xl items-center px-6 py-20 lg:px-8">
        <div className="grid w-full gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
          <div>
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-blue-100 bg-white/90 px-4 py-2 text-sm font-semibold text-blue-700 shadow-sm">
              <FaCheckCircle className="h-4 w-4 text-emerald-500" />
              {content.eyebrow}
            </div>
            <h1 className="max-w-2xl text-4xl font-bold leading-tight text-slate-900 md:text-5xl">
              {content.title}
            </h1>
            <p className="mt-5 max-w-xl text-lg leading-relaxed text-slate-600">
              {content.description}
            </p>
            <div className="mt-8 flex flex-col gap-4 sm:flex-row">
              <Link
                to="/fleet-planning-pro"
                className="inline-flex items-center justify-center rounded-lg bg-blue-700 px-8 py-4 text-base font-semibold text-white shadow-sm transition-colors hover:bg-blue-800"
              >
                {content.primaryCta}
                <FaArrowRight className="ml-2 h-4 w-4" />
              </Link>
              <Link
                to="/subscription"
                className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white px-8 py-4 text-base font-semibold text-slate-700 shadow-sm transition-colors hover:border-slate-300 hover:text-slate-900"
              >
                View plans
              </Link>
            </div>
          </div>

          <div className="relative">
            <div className="absolute -top-8 right-0 h-40 w-40 rounded-full bg-blue-100/70 blur-3xl" />
            <div className="absolute -bottom-8 left-0 h-40 w-40 rounded-full bg-teal-100/70 blur-3xl" />
            <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl">
              <div className="border-b border-slate-100 bg-gradient-to-r from-blue-600 to-indigo-500 px-8 py-8 text-white">
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-blue-100">
                  Receipt sent
                </p>
                <h2 className="mt-3 text-2xl font-bold">Next steps</h2>
                <p className="mt-2 text-sm leading-relaxed text-blue-100">
                  Your billing confirmation is on its way. You can continue straight into the platform.
                </p>
              </div>
              <div className="space-y-5 px-8 py-8">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                  <p className="text-sm font-semibold text-slate-900">What happens now</p>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    Use the button on the left to enter the product and continue with your workflow.
                  </p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-5">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 rounded-full bg-emerald-100 p-2 text-emerald-600">
                      <FaCheckCircle className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-900">Confirmation email</p>
                      <p className="mt-1 text-sm leading-relaxed text-slate-600">
                        Keep the email for your records. It includes the receipt and subscription details.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SubscriptionConfirmation;